import { IconButton, Skeleton, Stack, SxProps, useTheme } from '@mui/material';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { HXCIcon } from '../HXCIcon';
import { Text } from '../Text';
import { canScrollHorizontally } from './canScrollHorizontally';

export interface ScrollRowCarouselProps {
  children: ReactNode;
  loading?: boolean;
  skeletonCount?: number;
  loadingSize?: { height: string | number; width: string | number };
  title?: ReactNode;
  titleStyles?: SxProps;
  showScrollBar?: boolean;
  contentHeight?: string | number;
  showMobileArrows?: boolean;
  arrowsGutter?: string | number;
}

const ScrollArrow = ({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) => {
  const theme = useTheme();

  return (
    <IconButton
      onClick={onClick}
      aria-label={direction === 'left' ? 'scroll left' : 'scroll right'}
      sx={{
        bgcolor: theme.palette.silver.main,
        borderRadius: '50%',
        width: '2.75rem',
        height: '2.75rem',
        '&:hover': {
          bgcolor: theme.palette.lightGray.main,
        },
      }}
    >
      <HXCIcon name={direction === 'left' ? 'arrowLeftRg' : 'arrowRightRg'} color="darkGray" />
    </IconButton>
  );
};

export const ScrollRowCarousel = ({
  children,
  loading = false,
  skeletonCount = 5,
  loadingSize,
  title,
  showScrollBar = false,
  titleStyles,
  contentHeight = '26.25rem',
  showMobileArrows = false,
  arrowsGutter = '5rem',
}: ScrollRowCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScroll(false);
      return;
    }

    const updateCanScroll = () => setCanScroll(canScrollHorizontally(el));
    updateCanScroll();

    const resizeObserver =
      typeof ResizeObserver === 'function' ? new ResizeObserver(updateCanScroll) : null;
    resizeObserver?.observe(el);
    Array.from(el.children).forEach((child) => resizeObserver?.observe(child));

    window.addEventListener('resize', updateCanScroll);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateCanScroll);
    };
  }, [children, loading, skeletonCount]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = (scrollRef.current.offsetWidth * 3) / 4;
      const scrollValue = direction === 'left' ? -scrollAmount : scrollAmount;
      scrollRef.current.scrollBy({ left: scrollValue, behavior: 'smooth' });
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {title != null &&
          (typeof title === 'string' ? (
            <Text variant="h2" sx={titleStyles}>
              {title}
            </Text>
          ) : (
            <Stack sx={titleStyles}>{title}</Stack>
          ))}
        {canScroll && (
          <Stack
            display={{ xs: showMobileArrows ? 'flex' : 'none', md: 'flex' }}
            direction="row"
            spacing={1}
            marginRight={arrowsGutter}
          >
            <ScrollArrow direction="left" onClick={() => scroll('left')} />
            <ScrollArrow direction="right" onClick={() => scroll('right')} />
          </Stack>
        )}
      </Stack>
      <Stack
        direction="row"
        tabIndex={canScroll ? 0 : undefined}
        gap={2}
        ref={scrollRef}
        data-component="ScrollRowCarouselTrack"
        sx={{
          overflowX: canScroll ? (showScrollBar ? 'scroll' : 'auto') : 'hidden',
          overflowY: 'hidden',
          scrollbarWidth: showScrollBar ? 'thin' : 'none',
          overscrollBehaviorX: 'contain',
          WebkitOverflowScrolling: 'touch',
          height: contentHeight,
          width: '100%',
          alignItems: 'flex-start',
          ...(showScrollBar && canScroll && { paddingBottom: '0.5rem', boxSizing: 'border-box' }),
          '&:hover': {
            '&::-webkit-scrollbar': {
              height: showScrollBar ? '0.313rem' : 0,
            },
          },
          '& > *': {
            flexShrink: 0,
          },
          '&::-webkit-scrollbar': {
            height: 0,
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'lightGray.light',
            borderRadius: '0.5rem',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'darkGray.main',
            borderRadius: '0.5rem',
            '&:hover': {
              bgcolor: 'darkGray.dark',
            },
          },
        }}
      >
        {loading &&
          Array.from({ length: skeletonCount }).map((_, idx) => (
            <Skeleton
              key={idx}
              variant="rectangular"
              height={loadingSize?.height || '100%'}
              width={loadingSize?.width || '100%'}
              sx={{ borderRadius: '0.5rem' }}
            />
          ))}
        {!loading && children}
      </Stack>
    </Stack>
  );
};
